import { useState, useCallback, ReactNode } from 'react';
import { DashboardWidget } from './DashboardWidget';
import { COMMON_METRIC_OPTIONS, COMMON_VIEW_OPTIONS } from '../config';
import {
  ControlOption,
  DetailModalProps,
  ItemModalProps,
  AnalyticsQueryResult,
  ChartRenderer,
  TableRenderer,
} from './types';
import { AnalyticsErrorBoundary } from './AnalyticsErrorBoundary';

interface BaseAnalyticsCardProps<TData, TFilters> {
  title: string | ((metric: 'sessions' | 'minutes') => string);
  subtitle?: string;
  filters: TFilters;
  dataHook: (filters: TFilters) => AnalyticsQueryResult<TData[]>;

  renderChart: ChartRenderer<TData>;
  renderTable: TableRenderer<TData>;

  emptyMessage?: string;
  defaultMetric?: 'sessions' | 'minutes';
  metricOptions?: ControlOption<'sessions' | 'minutes'>[];
  viewOptions?: ControlOption<'chart' | 'table' | 'log'>[];
  dropdownViewOptions?: Array<'chart' | 'table'>;
  renderLog?: () => ReactNode;
  exportData?: unknown[] | ((activeView: 'chart' | 'table' | 'log') => unknown[] | undefined);
  exportFileName?: string | ((activeView: 'chart' | 'table' | 'log') => string);

  DetailModal?: React.ComponentType<DetailModalProps<TData, TFilters>>;
  ItemModal?: React.ComponentType<ItemModalProps<TData, TFilters>>;
  accent?: string;
}

export function BaseAnalyticsCard<TData, TFilters>({
  title,
  subtitle,
  filters,
  dataHook,
  renderChart,
  renderTable,
  emptyMessage = 'No data available',
  defaultMetric = 'sessions',
  metricOptions = COMMON_METRIC_OPTIONS,
  viewOptions = COMMON_VIEW_OPTIONS,
  dropdownViewOptions,
  renderLog,
  exportData,
  exportFileName,
  DetailModal,
  ItemModal,
  accent,
}: BaseAnalyticsCardProps<TData, TFilters>) {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TData | null>(null);

  const { data, isLoading, error } = dataHook(filters);

  const handleItemClick = useCallback((item: TData) => {
    setSelectedItem(item);
    setDetailModalOpen(false);
    setItemModalOpen(true);
  }, []);

  const handleCardClick = useCallback(() => {
    setDetailModalOpen(true);
  }, []);

  const chartRenderer = useCallback(
    (data: TData[], metric: string, onCardClick: () => void) => {
      return renderChart(data, metric, onCardClick, handleItemClick);
    },
    [renderChart, handleItemClick],
  );

  const tableRenderer = useCallback(
    (data: TData[], onItemClick?: (item: TData) => void) => renderTable(data, onItemClick),
    [renderTable],
  );

  const resolvedTitleForExport = typeof title === 'function' ? title(defaultMetric) : title;

  return (
    <>
      <DashboardWidget<TData>
        title={title}
        subtitle={subtitle}
        data={data}
        isLoading={isLoading}
        error={error}
        emptyMessage={emptyMessage}
        defaultMetric={defaultMetric}
        metricOptions={metricOptions}
        viewOptions={viewOptions}
        dropdownViewOptions={dropdownViewOptions}
        renderChart={chartRenderer}
        renderTable={tableRenderer}
        renderLog={renderLog}
        onItemClick={handleItemClick}
        onCardClick={handleCardClick}
        exportData={exportData}
        exportFileName={exportFileName || resolvedTitleForExport.toLowerCase().replace(/\s+/g, '-')}
        accent={accent}
      />

      {DetailModal && detailModalOpen ? (
        <AnalyticsErrorBoundary
          fallback={
            <div className="p-4 text-center text-sm text-[var(--anl-text-muted)]">
              Failed to load details. Please try again.
            </div>
          }
        >
          <DetailModal
            isOpen={detailModalOpen}
            onClose={() => setDetailModalOpen(false)}
            data={data || []}
            filters={filters}
            onItemClick={handleItemClick}
          />
        </AnalyticsErrorBoundary>
      ) : null}

      {ItemModal && itemModalOpen && selectedItem ? (
        <AnalyticsErrorBoundary
          fallback={
            <div className="p-4 text-center text-sm text-[var(--anl-text-muted)]">
              Failed to load item details. Please try again.
            </div>
          }
        >
          <ItemModal
            isOpen={itemModalOpen}
            onClose={() => setItemModalOpen(false)}
            item={selectedItem}
            filters={filters}
          />
        </AnalyticsErrorBoundary>
      ) : null}
    </>
  );
}
